/**
 * Motzei Shabbos London — "מוצאי שבת לאנדאן"
 * -------------------------------------------------------------------------
 * An Advanced-tab zman that is NOT a kosher-zmanim getter: it is derived from
 * two standard nightfall times that are,
 *
 *   • Tzeis (8.5 Degrees)           → `tzaisGeonim8Point5Degrees`
 *   • Tzeis (72 min / Rabbeinu Tam) → `tzais72`
 *
 * and picked between with this exact rule:
 *
 *   Case 1 — 8.5° is LATER than Rabbeinu Tam
 *            → use the raw 8.5° time (do NOT add 8 minutes).
 *   Case 2 — 8.5° is EARLIER than Rabbeinu Tam, but by 8 minutes or less
 *            → use 8.5° + 8 minutes.
 *   Case 3 — 8.5° is EARLIER than Rabbeinu Tam by more than 8 minutes
 *            → use standard Rabbeinu Tam (72 min).
 *
 * Boundaries, stated explicitly so every caller behaves identically:
 *   • The gap is measured on the exact instants (milliseconds), before any
 *     display rounding — display rounding is applied afterwards by the caller,
 *     exactly as it is for every other zman.
 *   • "8 minutes or less" is inclusive: a gap of exactly 8:00.000 is Case 2, a
 *     gap of 8:00.001 is Case 3.
 *   • When the two times are identical the gap is zero, so 8.5° is neither
 *     "later" (Case 1) nor "earlier" (Cases 2 / 3). Case 1 and Case 3 both
 *     resolve to the same instant there, so the raw 8.5° time is returned —
 *     the +8 minutes of Case 2 is never applied to a non-earlier 8.5°.
 *
 * Shared by the Zmanim_N editors' live preview (Add / Edit / Duplicate), the
 * live display, the Dashboard's embedded display, and the Minyanim-Roll editor
 * preview, so every layer computes the identical time.
 */

export const MOTZEI_SHABBOS_LONDON_KEY = 'motzeiShabbosLondon';

/** The two zmanim the rule is built from. */
export const MOTZEI_SHABBOS_LONDON_DEGREES_KEY = 'tzaisGeonim8Point5Degrees';
export const MOTZEI_SHABBOS_LONDON_RABBEINU_TAM_KEY = 'tzais72';

/** Minutes added to 8.5° in Case 2, and the Case 2 / Case 3 cutoff. */
export const MOTZEI_SHABBOS_LONDON_ADDED_MINUTES = 8;

const MS_PER_MINUTE = 60000;
const ADDED_MS = MOTZEI_SHABBOS_LONDON_ADDED_MINUTES * MS_PER_MINUTE;

/** True only for this derived key. */
export function isMotzeiShabbosLondonKey(key: unknown): boolean {
  return key === MOTZEI_SHABBOS_LONDON_KEY;
}

function isoToMs(iso: string): number {
  if (!iso) return Number.NaN;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : Number.NaN;
}

/**
 * Apply the three-case rule to two already-resolved ISO times.
 *
 * Both bases are required to evaluate the rule. If only one of them could be
 * computed for this date/location (extreme latitudes can leave 8.5° unreachable)
 * the available one is returned rather than nothing; if neither resolved, '' is
 * returned and the caller renders '--:--' as it does for any missing zman.
 */
export function resolveMotzeiShabbosLondonIso(
  tzeisDegreesIso: string,
  rabbeinuTamIso: string,
): string {
  const degreesIso = typeof tzeisDegreesIso === 'string' ? tzeisDegreesIso : '';
  const rabbeinuTamIsoStr = typeof rabbeinuTamIso === 'string' ? rabbeinuTamIso : '';
  const degreesMs = isoToMs(degreesIso);
  const rabbeinuTamMs = isoToMs(rabbeinuTamIsoStr);

  if (!Number.isFinite(degreesMs)) return Number.isFinite(rabbeinuTamMs) ? rabbeinuTamIsoStr : '';
  if (!Number.isFinite(rabbeinuTamMs)) return degreesIso;

  // How much EARLIER 8.5° is than Rabbeinu Tam (negative when 8.5° is later).
  const earlierByMs = rabbeinuTamMs - degreesMs;

  // Case 1 — 8.5° is later than (or exactly equal to) Rabbeinu Tam: raw 8.5°.
  if (earlierByMs <= 0) return degreesIso;

  // Case 2 — 8.5° is earlier by 8 minutes or less: 8.5° + 8 minutes.
  if (earlierByMs <= ADDED_MS) return new Date(degreesMs + ADDED_MS).toISOString();

  // Case 3 — 8.5° is earlier by more than 8 minutes: standard Rabbeinu Tam.
  return rabbeinuTamIsoStr;
}

/**
 * Resolve the zman through a caller-supplied `key → ISO` lookup, so each layer
 * feeds it the two base times from whatever source it already uses (a prebuilt
 * map, a ComplexZmanimCalendar getter, …) while the rule itself stays in one
 * place. Returns '' when neither base could be resolved.
 */
export function resolveMotzeiShabbosLondonFrom(getZmanIso: (key: string) => string): string {
  if (typeof getZmanIso !== 'function') return '';
  const read = (key: string): string => {
    try {
      const iso = getZmanIso(key);
      return typeof iso === 'string' ? iso : '';
    } catch (e) {
      return '';
    }
  };
  return resolveMotzeiShabbosLondonIso(
    read(MOTZEI_SHABBOS_LONDON_DEGREES_KEY),
    read(MOTZEI_SHABBOS_LONDON_RABBEINU_TAM_KEY),
  );
}
